"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const destinations = [
{
  flag: "🇦🇺",
  country: "Australia",
  unis: "42+ Universities",
  color: "#0A2D6E",
  rotate: "-3deg",
  delay: "0s",
  top: "12%",
  right: "-2%"
},
{
  flag: "🇬🇧",
  country: "United Kingdom",
  unis: "38+ Universities",
  color: "#8B1A1A",
  rotate: "2deg",
  delay: "1.5s",
  top: "54%",
  right: "1%"
},
{
  flag: "🇪🇺",
  country: "Europe",
  unis: "60+ Universities",
  color: "#1A4A1A",
  rotate: "-1.5deg",
  delay: "3s",
  top: "78%",
  right: "8%"
}];


const stats = [
{ value: "500+", label: "Students Placed Globally" },
{ value: "98%", label: "Visa Success Rate" },
{ value: "15+", label: "Countries Covered" }];


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    const reveals = heroRef?.current?.querySelectorAll(".reveal, .reveal-scale");
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden dot-pattern">
      
      {/* Background blobs */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full bg-crimson/5 blur-[120px] -translate-x-1/2 -translate-y-1/4 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-gold/10 blur-[100px] translate-x-1/3 translate-y-1/4 pointer-events-none" />
      <div className="max-w-site mx-auto px-6 lg:px-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-4 items-center">
          {/* Left: Content */}
          <div className="lg:col-span-7 relative z-10">
            {/* Label */}
            <div className="reveal delay-1">
              <span className="section-label">
                <span className="w-1.5 h-1.5 rounded-full bg-crimson inline-block" />
                India's Leading Overseas Education Consultancy
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-serif mt-6 mb-6 reveal delay-2"
              style={{
                fontSize: "clamp(2.8rem, 6.5vw, 5.2rem)",
                lineHeight: "0.95",
                letterSpacing: "-0.025em",
                fontWeight: 700
              }}>
              
              Your Dream University
              <br />
              Isn't Far.
              <br />
              It's Just{" "}
              <span className="gradient-text italic">One Right Decision Away.</span>
            </h1>

            {/* Subheadline */}
            <p
              className="text-lg text-ink-muted leading-relaxed max-w-lg mb-10 reveal delay-3"
              style={{ fontWeight: 400 }}>
              
              We don't just help you apply. We architect your entire study abroad journey; from choosing the right country and course, to visa approval, scholarship strategy, and landing safely on campus.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-14 reveal delay-4">
              <a href="#contact" className="btn-primary flex items-center gap-2">
                Book Your Free Strategy Session
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <a href="#services" className="btn-secondary">
                Explore Our Services
              </a>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-8 reveal delay-5">
              {stats?.map((s) =>
              <div key={s?.label}>
                  <p className="font-serif text-3xl font-bold text-crimson">{s?.value}</p>
                  <p className="text-sm text-ink-muted font-medium mt-0.5">{s?.label}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Visual */}
          <div className="lg:col-span-5 relative h-[520px] lg:h-[640px] reveal-scale delay-2">
            {/* Main image */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-[340px] h-[420px] lg:w-[380px] lg:h-[460px]">
                <div
                  className="w-full h-full rounded-[24px] overflow-hidden shadow-crimson-lg"
                  style={{ transform: "rotate(-1.5deg)" }}>
                  
                  <AppImage
                    src="/assets/images/world-map-student-destinations.png"
                    alt="World map highlighting famous international student destination cities including Toronto, London, Sydney, Melbourne, Auckland, Berlin, Munich and Dublin"
                    width={380}
                    height={460}
                    priority
                    className="w-full h-full object-cover" />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-crimson/30 via-transparent to-transparent" />
                </div>

                {/* Decorative corner accent */}
                <div
                  className="absolute -top-3 -right-3 w-24 h-24 rounded-[16px] border-2 border-gold/40"
                  style={{ transform: "rotate(2deg)" }} />
                
                <div
                  className="absolute -bottom-3 -left-3 w-16 h-16 rounded-[12px] bg-crimson/10 border border-crimson/20"
                  style={{ transform: "rotate(-2deg)" }} />
                
              </div>
            </div>

            {/* Floating destination cards */}
            {destinations?.map((d) =>
            <div
              key={d?.country}
              className="absolute glass-card rounded-[14px] px-4 py-3 float-card"
              style={{
                top: d?.top,
                right: d?.right,
                transform: `rotate(${d?.rotate})`,
                animationDelay: d?.delay,
                minWidth: "160px",
                zIndex: 20
              }}>
              
                <div className="flex items-center gap-2.5">
                  <span className="text-2xl">{d?.flag}</span>
                  <div>
                    <p className="text-xs font-bold text-ink">{d?.country}</p>
                    <p className="text-xs text-ink-muted">{d?.unis}</p>
                  </div>
                </div>
                <div
                className="mt-2 h-1 rounded-full opacity-60"
                style={{ background: d?.color, width: "70%" }} />
              
              </div>
            )}

            {/* Floating success badge */}
            <div
              className="absolute bottom-16 left-0 glass-card rounded-[12px] px-4 py-3 float-card-alt"
              style={{ animationDelay: "2s" }}>
              
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-crimson flex items-center justify-center text-white text-sm">✓</div>
                <div>
                  <p className="text-xs font-bold text-ink">Visa Approved!</p>
                  <p className="text-xs text-ink-muted">Priya S. → Toronto</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <p className="text-xs font-medium text-ink-muted tracking-widest uppercase">Scroll</p>
        <div className="w-px h-8 bg-crimson animate-pulse" />
      </div>
    </section>);

}